import { Alert, App, Flex, Form, Input, Space, theme, Typography } from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPowerOff, faShieldHalved } from '@fortawesome/free-solid-svg-icons';
import { InsuranceCarrierLogo } from 'Components/commonComponents/InsuranceCarrierLogo';
import { CredentialsFormModal } from './CredentialsFormModal';
import { SelectSoftsegurosInsuranceCarrier } from '@features/insuranceCarriers/components/SelectSoftsegurosInsuranceCarrier';
import { useModal } from '@shared/hooks/useModal';
import { getMenuItems } from './getMenuItems';
import { getErrorMessage } from '@shared/utils/errors/getErrorMessage';
import { insuranceCarriersQueryKeys, useCreateInsuranceCarrier } from '@features/insuranceCarriers/hooks/useInsuranceCarriers';
import { UploadCertificateModal } from './UploadCertificateModal';
import { ActionsDropdown as ActionsDropdownBase } from '@/shared/components/ActionsDropdown';

const { Text, Paragraph } = Typography;

export const ActionsDropdown = ({ record, compact = false }) => {
  const [addInsuranceCarrierForm] = Form.useForm();
  const { modal, notification } = App.useApp();
  const { token: themeToken } = theme.useToken();

  const createInsuranceCarrier = useCreateInsuranceCarrier();
  const queryClient = useQueryClient();

  const credentialsFormModal = useModal({ readOnly: false });
  const uploadCertificateModal = useModal();

  const handleUpdateCredentials = () => {
    credentialsFormModal.open({ readOnly: false });
  };

  const handleViewCredentials = () => {
    credentialsFormModal.open({ readOnly: true });
  };

  const handleUploadCertificate = () => {
    uploadCertificateModal.open();
  };

  const handleAddInsuranceCarrier = () => {
    modal.confirm({
      icon: null,
      title: (
        <Space align='baseline'>
          <FontAwesomeIcon icon={faShieldHalved} color={themeToken.colorPrimary} />
          <span>¿Desea agregar la aseguradora {record.name}?</span>
        </Space>
      ),
      content: (<>
        <Flex justify='center' style={{ marginBlock: '1em' }}>
          <InsuranceCarrierLogo name={record.name} logoUrl={record.logoUrl} height='6em' disableTooltip />
        </Flex>
        <Paragraph>
          Se debe vincular esta aseguradora con su equivalente en Softseguros para habilitar funciones que dependen de la integración entre ambas plataformas.
        </Paragraph>
        <Form form={addInsuranceCarrierForm} layout='vertical' clearOnDestroy>
          <Form.Item
            name='softsegurosInsuranceCarrierId'
            label={<Text>Aseguradora equivalente en Softseguros:</Text>}
            tooltip='Si la aseguradora no aparece en la lista, es posible que aún no haya sido creada o se encuentre inactiva en Softseguros. Verifíquelo y vuelva a intentarlo.'
            rules={[{ required: true }]}>
            <SelectSoftsegurosInsuranceCarrier />
          </Form.Item>
        </Form>
        <Alert
          message={<Text strong>Importante</Text>}
          description={<Text>Una vez agregada, deberá configurar las credenciales y activar la aseguradora para que esté disponible en la plataforma. Tenga en cuenta que no podrá eliminarla posteriormente, solo podrá inactivarla si es necesario.</Text>}
          type='warning'
        />
      </>),
      okText: 'Sí, agregar',
      onOk: async () => {
        const { softsegurosInsuranceCarrierId } = await addInsuranceCarrierForm.validateFields();

        await createInsuranceCarrier.mutateAsync({ availableInsuranceCarrierId: record.id, softsegurosInsuranceCarrierId }, { // record.id es el id de la aseguradora disponible que se obtiene en useGetAvailableInsuranceCarriers
          onSuccess: () => {
            // Invalida todas las consultas que contengan insuranceCarriersQueryKeys.list() y insuranceCarriersQueryKeys.available() en su queryKey para actualizar sus datos
            void queryClient.invalidateQueries({ queryKey: insuranceCarriersQueryKeys.list() });
            void queryClient.invalidateQueries({ queryKey: insuranceCarriersQueryKeys.available() });

            notification.success({
              message: <Text strong>Aseguradora <Text italic>{record.name}</Text> agregada</Text>,
              description: <>¡La aseguradora se ha agregado correctamente!</>,
            });
          },
          onError: (error) => {
            notification.error({
              message: <Text strong>Aseguradora <Text italic>{record.name}</Text> no agregada</Text>,
              description: getErrorMessage(error),
            });
          },
        });
      },
      width: '35em',
    });
  };

  const handleCancel = () => {
    credentialsFormModal.close();
  };

  const actions = {
    onUpdateCredentials: handleUpdateCredentials,
    onViewCredentials: handleViewCredentials,
    onUploadCertificate: handleUploadCertificate,
    onAddInsuranceCarrier: handleAddInsuranceCarrier,
  };

  const isCreated = !!record.createdAt; // Verifica si la aseguradora ya fue creada (tiene createdAt)
  const items = getMenuItems({ record, actions, isCreated });

  return (<>
    <ActionsDropdownBase items={items} compact={compact} />
    {isCreated && (<>
      <CredentialsFormModal record={record} open={credentialsFormModal.isOpen} onCancel={handleCancel} readOnly={credentialsFormModal.data.readOnly} />
      {record.certificates?.length > 0 && <UploadCertificateModal record={record} open={uploadCertificateModal.isOpen} onCancel={uploadCertificateModal.close} />}
    </>)}
  </>);
};
